import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Listbox, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";

import UserIcon from "theme/icons/UserIcon";
import ExpandDownIcon from "theme/icons/ExpandDown";
import PinLogin from "views/components/auth/login/PinLogin";
import { IoIosArrowBack } from "react-icons/io";
import { getAllExistingUsers } from "actions/users";
import { APP_VERSION } from "helpers/constants";

const Auth = ({ navigatePage }) => {
	const dispatch = useDispatch();

	const localUsers = useSelector((state) => state.LocalUsers);
	const daffiUsers = useSelector((state) => state.DaffiUsers);

	const [selectedUser, setSelectedUser] = useState(null);
	const [showRegisterType, setShowRegisterType] = useState(false);

	useEffect(() => {
		dispatch(getAllExistingUsers());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (localUsers?.length) setSelectedUser(localUsers[0]);
		else if (daffiUsers?.length) setSelectedUser(daffiUsers[0]);
	}, [localUsers, daffiUsers]);

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="h-screen flex items-center justify-center">
				<div className="flex w-full items-start justify-center">
					{showRegisterType ? (
						<div className="flex flex-col w-[50%] items-center justify-center space-y-10 px-14">
							<h1 className="text-xl font-bold text-dark-blue">What Type of installation you want?</h1>
							<Link
								to="/Register"
								state={{ registrationType: "local" }}
								className={`flex flex-col w-fit items-center justify-center rounded-xl px-6 py-4 bg-dark-blue hover:shadow-lg`}
							>
								<p className={`text-sm font-bold text-white`}>Just want a local</p>
								<p className={`text-sm font-bold text-white`}>
									Daffi<span className="italic">Wallet</span>
								</p>
								<p className={`text-sm font-bold text-white`}>without a Daffi</p>
								<p className={`text-sm font-bold text-white`}>user account</p>
							</Link>
							<Link
								to="/Register"
								state={{ registrationType: "daffi" }}
								className={`flex flex-col w-fit items-center justify-center rounded-xl px-6 py-4 bg-dark-blue hover:shadow-lg`}
							>
								<p className={`text-sm font-bold text-white`}>I want to create</p>
								<p className={`text-sm font-bold text-white`}>a Daffi user</p>
								<p className={`text-sm font-bold text-white`}>account</p>
							</Link>
							<div className="flex w-full items-center justify-center">
								<IoIosArrowBack
									className="text-4xl text-dark-blue cursor-pointer"
									onClick={() => setShowRegisterType(false)}
								/>
							</div>
						</div>
					) : (
						<div
							className={`flex flex-col lg:w-[30%] md:w-[50%] items-center justify-center ${
								(localUsers?.length || daffiUsers?.length) && "space-y-14"
							}`}
						>
							<div className="flex flex-col w-full items-center justify-center space-y-7">
								<div className="flex w-full items-center justify-center space-x-2">
									<div className={`w-8`} />
									<img
										className="w-16"
										src={`${process.env.PUBLIC_URL}/media/changepin-pastelgreen.png`}
										alt="logo"
									/>
									{daffiUsers || localUsers ? (
										<div className="flex flex-col items-start justify-start">
											<h1 className={`text-3xl font-bold text-dark-blue`}>
												Daffi<span className="italic">Wallet</span>
											</h1>
											<h1 className={`text-3xl font-bold text-dark-blue`}>Welcome back</h1>
											<p className="text-sm font-bold text-dark-blue">Enter your 4-digit PIN</p>
										</div>
									) : (
										<div className="flex flex-col items-start justify-start">
											<h1 className={`text-3xl font-bold text-dark-blue`}>Welcome to</h1>
											<h1 className={`text-3xl font-bold text-dark-blue`}>
												Daffi<span className="italic">Wallet</span>
											</h1>
										</div>
									)}
								</div>

								<div className="flex flex-col w-full items-center justify-center space-y-5">
									{(daffiUsers || localUsers) && (
										<Listbox value={selectedUser} onChange={setSelectedUser}>
											<div className="relative mt-1 w-full">
												<div className="flex space-x-5 items-center">
													{selectedUser && selectedUser["profile-image"] ? (
														<img
															className={`w-8 h-8 object-cover object-center rounded-lg`}
															src={selectedUser["profile-image"]}
															alt="temp"
														/>
													) : (
														<UserIcon className={`w-8 h-8`} fill="#003D79" />
													)}

													<Listbox.Button
														className={`relative w-full cursor-default rounded-lg bg-dark-blue bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
													>
														{selectedUser ? (
															<div className="flex flex-col w-full items-start justify-start space-y-1">
																<h3 className={`font-medium text-sm text-dark-blue`}>
																	{selectedUser?.username}
																</h3>
																<p className={`text-xs italic text-dark-blue`}>
																	{selectedUser?.loginType} account
																</p>
															</div>
														) : (
															<h3 className={`font-medium text-sm text-dark-blue`}>
																Select A User
															</h3>
														)}

														<span
															className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-dark-blue`}
														>
															<ExpandDownIcon className="w-4" fill="#003D79" />
														</span>
													</Listbox.Button>
												</div>
												<Transition
													as={Fragment}
													leave="transition ease-in duration-100"
													leaveFrom="opacity-100"
													leaveTo="opacity-0"
												>
													<Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-base shadow-lg">
														{localUsers?.map((user, index) => (
															<Listbox.Option
																key={index}
																className={({ active }) =>
																	`relative cursor-default select-none py-2 pl-10 rounded-lg pr-4 ${
																		active ? "bg-amber-100 text-amber-900" : "text-gray-900"
																	}`
																}
																value={user}
															>
																<div
																	className={`flex w-full items-center justify-center space-x-5`}
																>
																	{user["profile-image"] ? (
																		<img
																			className={`w-8 h-8 object-cover object-center rounded-lg`}
																			src={user["profile-image"]}
																			alt="temp"
																		/>
																	) : (
																		<UserIcon className={`w-8 h-8`} fill="#003D79" />
																	)}

																	<div
																		// to="/Login"
																		// state={{ loginType: user?.loginType, activeUser: user }}
																		className="flex flex-col w-full items-start justify-start rounded-lg space-y-1"
																	>
																		<h3 className={`font-medium text-sm text-dark-blue`}>
																			{user?.username}
																		</h3>
																		<p className={`text-xs italic text-dark-blue`}>
																			{user?.loginType} account
																		</p>
																	</div>
																</div>
															</Listbox.Option>
														))}

														{daffiUsers?.map((user, index) => (
															<Listbox.Option
																key={index}
																className={({ active }) =>
																	`relative cursor-default select-none py-2 pl-10 pr-4 ${
																		active ? "bg-amber-100 text-amber-900" : "text-gray-900"
																	}`
																}
																value={user}
															>
																<div
																	className={`flex w-full items-center justify-center space-x-5`}
																>
																	{user["profile-image"] ? (
																		<img
																			className={`w-8 h-8 object-cover object-center rounded-lg`}
																			src={user["profile-image"]}
																			alt="temp"
																		/>
																	) : (
																		<UserIcon className={`w-8 h-8`} fill="#003D79" />
																	)}

																	<div
																		// to="/Login"
																		// state={{ loginType: user?.loginType, activeUser: user }}
																		className="flex flex-col w-full items-start justify-start rounded-lg space-y-1"
																	>
																		<h3 className={`font-medium text-sm text-dark-blue`}>
																			{user?.username}
																		</h3>
																		<p className={`text-xs italic text-dark-blue`}>
																			{user?.loginType} account
																		</p>
																	</div>
																</div>
															</Listbox.Option>
														))}
													</Listbox.Options>
												</Transition>
											</div>
										</Listbox>
									)}

									{selectedUser?.username && (
										<div className="flex space-x-5">
											<div className={`w-8`} />
											<PinLogin
												user={selectedUser}
												loginType={selectedUser?.loginType}
												navigatePage={navigatePage}
											/>
										</div>
									)}
								</div>
							</div>

							<div className="flex w-full items-center justify-center space-x-5">
								<div className={`w-8`} />
								<div className="flex flex-col w-full items-center justify-center space-y-6">
									{/* <Link
										to="/Login"
										state={{ loginType: "daffi", activeUser: "reinstallFromDaffiAccount" }}
										className={`flex flex-col w-full items-center justify-center`}
									>
										<p className={`text-sm font-bold text-dark-blue`}>I want to login using my Daffi</p>
										<p className={`text-sm font-bold text-dark-blue`}>username and password</p>
									</Link> */}

									{/* <div
										className={`flex flex-col w-full items-center justify-center cursor-pointer`}
										onClick={() => setShowRegisterType(true)}
									>
										<p className={`text-sm font-bold text-dark-blue`}>
											I want to create a Daffi<span className="italic">Wallet</span>
										</p>
										<p className={`text-sm font-bold text-dark-blue`}>on this device</p>
									</div> */}
									<Link
										to="/Register"
										state={{ registrationType: "local" }}
										className={`flex flex-col w-fit items-center justify-center rounded-xl px-6 py-4 hover:shadow-lg`}
									>
										<p className={`text-sm font-bold text-dark-blue`}>
											I want to create {localUsers?.length || daffiUsers?.length ? "another" : "a"} Daffi
											<span className="italic">Wallet</span>
										</p>
										<p className={`text-sm font-bold text-dark-blue`}>on this device</p>
									</Link>

									<div className="flex flex-col w-full items-center justify-center">
										<p className={`text-sm font-bold text-dark-blue`}>Version</p>
										<p className={`text-sm font-bold text-dark-blue`}>
											Daffi
											<span className="italic">Wallet</span> Web {APP_VERSION}
										</p>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</Transition>
	);
};

export default Auth;
